import * as d3 from 'd3'
import * as dc from 'dc'
import { HOUR_LABELS } from '@/utils/flowSightHourBuckets'
import { formatCompactAmount } from '@/utils/utils'
import { getChartTooltip, escapeHtml } from '../runtime/chartTooltip.js'
import {
  hasRows,
  totalVolumeOf,
  averageTicketOf,
  hourBandMix,
  cumulativeSeries,
} from '../calculations/clientPortfolio.js'

// "Biggest clients", after the Economist's per-capita/absolute split: the bars
// rank by a PER-UNIT metric (average ticket) while the box and the bubble rail
// rank by the ABSOLUTE total. The two orders genuinely differ here - the client
// with the largest total does 428 small tickets, the third-largest does 62 huge
// ones - and showing only one of them hides that.
//
// Hand-drawn into a single SVG and registered as a pseudo-chart, same as the
// portfolio panel beside it: dozens of dc instances would not align and would
// not share a scale.

const ROW_H = 26
const NAME_W = 168
const TOTAL_W = 72
const GUTTER = 10
const AXIS_H = 26
// Rail geometry moves as a unit: the row must clear a full 2*R circle, and the
// column must hold that circle plus the two lines of text beside it.
const MAX_RAIL_R = 60
const RAIL_PAD = 8
// Floor for a rail row: the two lines of text beside the circle need this much
// even when the circle itself is tiny.
const RAIL_TEXT_MIN = 26
// Piso do circulo: 9,8px de diametro, o tamanho que a menor bolha tinha quando
// MAX_RAIL_R era 30. Dobrar o teto nao deve arrastar a cauda junto - abaixo
// disso o circulo deixa de ser lido como area e vira ponto.
// Nao conflita com RAIL_TEXT_MIN: 9,8 + RAIL_PAD = 17,8px, ainda abaixo dos
// 26px que as duas linhas de texto exigem, entao a altura minima da linha
// continua sendo ditada pelo texto.
const MIN_RAIL_D = 9.8
const MIN_RAIL_R = MIN_RAIL_D / 2
// Largura do texto ao lado do circulo. Cresceu junto com a coluna: com 140 os
// nomes eram cortados em 15 caracteres ("VOLKSWAGEN BR I..."), e a coluna larga
// existe justamente para caber mais.
const RAIL_TEXT_W = 250
const RAIL_GAP = 12
// O bloco e o que de fato se ve: circulo + respiro + texto. A coluna e MAIOR
// que ele de proposito, e a sobra e dividida pelos dois lados (railInset), para
// o grafico de bolhas ficar centralizado na coluna em vez de colado a esquerda.
const RAIL_BLOCK_W = 2 * MAX_RAIL_R + RAIL_GAP + RAIL_TEXT_W
const RAIL_W = 600
const railInset = () => Math.max(0, (RAIL_W - RAIL_BLOCK_W) / 2)
// Plot area is SERIES_H minus 38px of axis/label padding. At 190 the eight
// cumulative lines converged into a band a few pixels tall near the origin.
const SERIES_H = 320
// Left at 8 regardless of the top-N picker: more lines than this on one axis
// stops being readable, whatever the user selected for the bars.
const TOP_SERIES = 8
const HEADER_H = 22

// Sequential band palette: short tenors light, long tenors dark. A categorical
// palette would suggest the bands are unordered, and they are not.
const bandColor = (i) => d3.interpolateBlues(0.25 + (i / (HOUR_LABELS.length - 1)) * 0.6)

// topN() returns a number, or null for "all". It selects by TOTAL volume - the
// page is about the biggest clients - and the bars then order that set by
// average ticket, so both notions of "biggest" survive the cut.
export function createDealerRankingPanel ({ el, chartGroup, group, t, uid, topN }) {
  const host = el
  if (!host) return null

  const tooltip = getChartTooltip()

  const rowsOf = () => group.all().filter((d) => hasRows(d.value))

  const collectDays = (rows) => {
    const days = new Set()
    rows.forEach(({ value }) => value.byDay.forEach((_, day) => days.add(day)))
    return Array.from(days).sort()
  }

  const info = (label, v) =>
    `<div><span style="color:var(--muted,#9aa3af)">${escapeHtml(label)}:</span> ${escapeHtml(v)}</div>`

  const clientTip = (key, value, extra) => [
    `<div style="font-weight:700;margin-bottom:4px">${escapeHtml(key)}</div>`,
    info(t('flowSightPortfolio.negotiatedVolume'), `R$ ${formatCompactAmount(totalVolumeOf(value))}`),
    info(t('flowSightPortfolio.operations'), value.cnt),
    info(t('flowSightDealers.avgTicket'), `R$ ${formatCompactAmount(averageTicketOf(value))}`),
    extra || '',
  ].join('')

  const render = () => {
    const rows = rowsOf()
    const days = collectDays(rows)
    const width = Math.max(720, host.clientWidth || 900)

    d3.select(host).selectAll('svg.dealer-panel').remove()
    if (!rows.length) return

    const limit = (typeof topN === 'function' ? topN() : topN) || null
    const byTotal = rows.slice().sort((a, b) => totalVolumeOf(b.value) - totalVolumeOf(a.value))
    const selected = limit ? byTotal.slice(0, limit) : byTotal
    const byTicket = selected.slice().sort((a, b) => averageTicketOf(b.value) - averageTicketOf(a.value))

    const barsW = Math.max(240, width - RAIL_W - GUTTER * 2 - NAME_W - TOTAL_W - GUTTER * 2)
    // Two independent columns, not two rows sharing a band. The left column
    // stacks bars then series; the rail runs down the right for the full height
    // of the panel. Forcing them onto a shared top band left a gap under the
    // bars as tall as the rail's overhang - 478px at top 12, 2410px at "all".
    const barsHeight = HEADER_H + AXIS_H + byTicket.length * ROW_H + AXIS_H
    // Rail rows are sized to their own circle instead of to the largest one.
    // A constant row height reserved 2*MAX_RAIL_R for every entry, so the tail
    // of the list - where the circles are small - was mostly empty space: 54%
    // of the rail's height at "all", 27% even at top 12.
    const railR = d3.scaleSqrt()
      .domain([0, d3.max(selected, ({ value }) => totalVolumeOf(value)) || 1])
      .range([0, MAX_RAIL_R])
    const railLayout = []
    let railY = HEADER_H + AXIS_H
    selected.forEach((d) => {
      const r = Math.max(MIN_RAIL_R, railR(totalVolumeOf(d.value)))
      const rowH = Math.max(2 * r + RAIL_PAD, RAIL_TEXT_MIN)
      railLayout.push({ ...d, r, cy: railY + rowH / 2 })
      railY += rowH
    })
    const railHeight = railY
    const leftHeight = barsHeight + SERIES_H + 30
    const height = Math.max(leftHeight, railHeight)

    const svg = d3.select(host)
      .append('svg')
      .attr('class', 'dealer-panel')
      .attr('width', width)
      .attr('height', height)

    // ---------------------------------------------------------------- bars
    const x = d3.scaleLinear()
      .domain([0, d3.max(byTicket, ({ value }) => averageTicketOf(value)) || 1])
      .range([0, barsW])
    const barsLeft = NAME_W + GUTTER

    // The axis is drawn twice, above and below the bars. Only the TOP one draws
    // gridlines: they run downward across the bars, so repeating them from the
    // bottom axis sent a second set 290px into the cumulative-volume chart
    // underneath.
    const drawTicketAxis = (y, withGrid) => {
      const g = svg.append('g').attr('transform', `translate(${barsLeft},${y})`)
      x.ticks(5).forEach((v) => {
        g.append('text')
          .attr('x', x(v)).attr('y', 0).attr('text-anchor', 'middle')
          .attr('style', 'font-size:10px; fill:var(--muted, #9aa3af)')
          .text(v === 0 ? '0' : `R$ ${formatCompactAmount(v)}`)
        if (!withGrid) return
        g.append('line')
          .attr('x1', x(v)).attr('x2', x(v))
          .attr('y1', 6).attr('y2', byTicket.length * ROW_H + 6)
          .attr('stroke', 'var(--border, #2a2e36)').attr('stroke-width', 1).attr('opacity', 0.35)
      })
    }
    // Column headers: text only. They carried a tinted background band each,
    // and with just GUTTER between them the two bands merged into one strip -
    // "VOLUME TOTAL" read as if it sat inside the ticket column's box. The
    // reference tints ONE callout column, not every header.
    const header = (x0, w, label, anchor) => {
      svg.append('text')
        .attr('x', anchor === 'end' ? x0 + w : x0)
        .attr('y', HEADER_H - 12)
        .attr('text-anchor', anchor)
        .attr('style', 'font-size:10px; font-weight:700; fill:var(--muted, #9aa3af)')
        .text(label)
    }
    header(barsLeft, barsW, t('flowSightDealers.axisTicket'), 'start')
    header(barsLeft + barsW + GUTTER, TOTAL_W, t('flowSightDealers.colTotal'), 'end')

    drawTicketAxis(HEADER_H + AXIS_H - 10, true)

    const barsG = svg.append('g').attr('transform', `translate(0,${HEADER_H + AXIS_H})`)
    byTicket.forEach(({ key, value }, i) => {
      const y = i * ROW_H
      const row = barsG.append('g').attr('transform', `translate(0,${y})`)

      row.append('text')
        .attr('x', NAME_W).attr('y', ROW_H / 2 + 4).attr('text-anchor', 'end')
        .attr('style', 'font-size:11px; fill:#fff !important')
        .text(key.length > 24 ? `${key.slice(0, 23)}…` : key)

      // Segments decompose the SAME quantity the axis measures: each band's
      // share of volume divided by the client's RFQ count, so the widths add up
      // to the average ticket exactly.
      let acc = 0
      hourBandMix(value).forEach((b) => {
        if (b.value <= 0) return
        const contrib = b.value / value.cnt
        const x0 = x(acc)
        const x1 = x(acc + contrib)
        acc += contrib
        row.append('rect')
          .attr('x', barsLeft + x0).attr('y', 4)
          .attr('width', Math.max(0.5, x1 - x0)).attr('height', ROW_H - 8)
          .attr('fill', bandColor(b.band))
          .on('mouseover', () => tooltip.show(clientTip(key, value,
            `<div style="margin-top:4px">${escapeHtml(b.label)}: R$ ${escapeHtml(formatCompactAmount(b.value))}</div>`), d3.event))
          .on('mousemove', () => tooltip.move(d3.event))
          .on('mouseout', () => tooltip.hide())
      })

      row.append('text')
        .attr('x', barsLeft + barsW + GUTTER + TOTAL_W).attr('y', ROW_H / 2 + 4).attr('text-anchor', 'end')
        .attr('style', 'font-size:10px; font-weight:600; fill:#fff !important')
        .text(`R$ ${formatCompactAmount(totalVolumeOf(value))}`)
    })
    drawTicketAxis(HEADER_H + AXIS_H + byTicket.length * ROW_H + 14, false)

    // ---------------------------------------------------------------- rail
    const railLeft = width - RAIL_W
    const railG = svg.append('g').attr('transform', `translate(${railLeft},0)`)
    railG.append('text')
      .attr('x', railInset()).attr('y', 12)
      .attr('style', 'font-size:10px; font-weight:700; fill:var(--muted, #9aa3af)')
      .text(t('flowSightDealers.railTitle'))

    const railX = railInset()
    const railTextX = railX + 2 * MAX_RAIL_R + RAIL_GAP
    // ~6.2px por caractere a 10px de fonte - o corte acompanha RAIL_TEXT_W em
    // vez de ser um numero fixo, senao alargar a coluna nao mostra mais nome.
    const railNameMax = Math.max(12, Math.floor(RAIL_TEXT_W / 6.2))
    railLayout.forEach(({ key, value, r, cy: y }) => {
      railG.append('circle')
        .attr('cx', railX + MAX_RAIL_R).attr('cy', y).attr('r', r)
        .attr('fill', bandColor(2)).attr('fill-opacity', 0.75)
        .on('mouseover', () => tooltip.show(clientTip(key, value), d3.event))
        .on('mousemove', () => tooltip.move(d3.event))
        .on('mouseout', () => tooltip.hide())
      railG.append('text')
        .attr('x', railTextX).attr('y', y - 2)
        .attr('style', 'font-size:10px; fill:#fff !important')
        .text(key.length > railNameMax ? `${key.slice(0, railNameMax - 1)}…` : key)
      railG.append('text')
        .attr('x', railTextX).attr('y', y + 9)
        .attr('style', 'font-size:9px; fill:var(--muted, #9aa3af)')
        .text(`R$ ${formatCompactAmount(totalVolumeOf(value))}`)
    })

    // -------------------------------------------------------------- series
    const sG = svg.append('g').attr('transform', `translate(0,${barsHeight + 16})`)
    sG.append('text')
      .attr('x', barsLeft).attr('y', 0)
      .attr('style', 'font-size:10px; font-weight:700; fill:var(--muted, #9aa3af)')
      .text(t('flowSightDealers.seriesTitle'))

    const seriesRows = selected.slice(0, TOP_SERIES)
    const sx = d3.scalePoint().domain(days).range([barsLeft, barsLeft + barsW]).padding(0.5)
    const sy = d3.scaleLinear()
      .domain([0, d3.max(seriesRows, ({ value }) => d3.max(cumulativeSeries(value, days), (p) => p.value) || 0) || 1])
      .range([SERIES_H - 24, 14])

    sy.ticks(4).forEach((v) => {
      sG.append('line')
        .attr('x1', barsLeft).attr('x2', barsLeft + barsW)
        .attr('y1', sy(v)).attr('y2', sy(v))
        .attr('stroke', 'var(--border, #2a2e36)').attr('opacity', 0.3)
      sG.append('text')
        .attr('x', barsLeft - 6).attr('y', sy(v) + 3).attr('text-anchor', 'end')
        .attr('style', 'font-size:9px; fill:var(--muted, #9aa3af)')
        .text(v === 0 ? '0' : `R$ ${formatCompactAmount(v)}`)
    })

    const line = d3.line().x((p) => sx(p.day)).y((p) => sy(p.value)).curve(d3.curveMonotoneX)
    seriesRows.forEach(({ key, value }, i) => {
      const series = cumulativeSeries(value, days)
      if (series.length < 2) return
      const color = d3.interpolateBlues(0.35 + (i / Math.max(1, TOP_SERIES - 1)) * 0.55)
      sG.append('path')
        .attr('d', line(series)).attr('fill', 'none')
        .attr('stroke', color).attr('stroke-width', 1.6)
        .on('mouseover', () => tooltip.show(clientTip(key, value), d3.event))
        .on('mousemove', () => tooltip.move(d3.event))
        .on('mouseout', () => tooltip.hide())
      const last = series[series.length - 1]
      sG.append('text')
        .attr('x', sx(last.day) + 4).attr('y', sy(last.value) + 3)
        .attr('style', `font-size:9px; fill:${color}`)
        .text(key.length > 14 ? `${key.slice(0, 13)}…` : key)
    })

    // First and last day only: 20 trading days would collide as labels.
    ;[days[0], days[days.length - 1]].forEach((d, i) => {
      if (!d) return
      sG.append('text')
        .attr('x', sx(d)).attr('y', SERIES_H - 6)
        .attr('text-anchor', i === 0 ? 'start' : 'end')
        .attr('style', 'font-size:9px; fill:var(--muted, #9aa3af)')
        .text(d.split('-').reverse().slice(0, 2).join('/'))
    })
  }

  const chartObj = {
    render: () => render(),
    redraw: () => render(),
    filterAll: () => chartObj,
    filters: () => [],
    anchorName: () => `flowsightDealers_${uid}`,
  }

  dc.registerChart(chartObj, chartGroup)
  render()
  return chartObj
}
